import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('subhack_token'));
    const [loading, setLoading] = useState(true);

    const setAuthToken = useCallback((newToken) => {
        if (newToken) {
            localStorage.setItem('subhack_token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        } else {
            localStorage.removeItem('subhack_token');
            delete axios.defaults.headers.common['Authorization'];
        }
        setToken(newToken);
    }, []);

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const response = await axios.get(`${API}/auth/me`);
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            setAuthToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token, setAuthToken]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const register = async (email, password) => {
        const response = await axios.post(`${API}/auth/register`, {
            email,
            password
        });
        setAuthToken(response.data.access_token);
        setUser(response.data.user);
        return response.data;
    };

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, {
            email,
            password
        });
        setAuthToken(response.data.access_token);
        setUser(response.data.user);
        return response.data;
    };

    const logout = () => {
        setAuthToken(null);
        setUser(null);
    };

    const isAuthenticated = !!user;
    const isPro = user?.subscription_status === 'pro';
    const isAdmin = user?.role === 'admin';
    const isFree = user?.subscription_status === 'free';

    const value = {
        user,
        token,
        loading,
        isAuthenticated,
        isPro,
        isAdmin,
        isFree,
        register,
        login,
        logout,
        refreshUser: fetchUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
